/**
 * Screen 7 — checkout.
 *
 * "No new charges appear here." Everything on this screen was already shown
 * on the seat screen; the only new thing is the button. The service fee is
 * itemised because the member saw it there too — and when the committee sets
 * `service_fee_absorbed`, the line disappears rather than showing Rp0.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { LocalisedField } from "../components/Localised";
import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api, errorCode, OfflineError, type BookingOut } from "../lib/api";
import { formatDateTime, formatIdr } from "../lib/format";

export default function Checkout({ locale }: { locale: Locale }) {
  const { ref = "" } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<BookingOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .booking(ref)
      .then(setBooking)
      .catch((err) =>
        setError(err instanceof OfflineError ? t("error.network") : t("error.generic")),
      );
  }, [ref, t]);

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      const out = await api.checkout(ref);
      // Hand off to the provider's own page. The booking is PENDING_PAYMENT
      // from here, and only the webhook can make it PAID — coming back
      // through the return URL proves nothing on its own.
      window.location.href = out.redirect_url;
    } catch (err) {
      const code = errorCode(err);
      setError(code ? t(`error.${code}`, { defaultValue: t("error.generic") }) : t("error.generic"));
      setBusy(false);
    }
  }

  if (!booking) {
    return (
      <Shell area="programs" title={t("checkout.title")}>
        {error ? <ErrorNote>{error}</ErrorNote> : <Note>{t("common.loading")}</Note>}
      </Shell>
    );
  }

  const tickets = booking.seats.reduce((sum, seat) => sum + seat.price_idr, 0);

  return (
    <Shell area="programs" title={t("checkout.title")}>
      <LocalisedField
        content={booking.program.text}
        field="name"
        as="h2"
        className="screen__subtitle"
      />
      <p>{formatDateTime(booking.program.date_time, locale)}</p>
      <p>{booking.program.venue}</p>
      <p>
        {t("ticket.seat")}: <b>{booking.seats.map((s) => s.label).join(", ")}</b>
      </p>

      <dl className="totals">
        <dt>{t("checkout.ticket")}</dt>
        <dd className="money">{formatIdr(tickets)}</dd>
        {!booking.fee_absorbed && booking.service_fee_idr > 0 && (
          <>
            <dt>{t("checkout.service_fee")}</dt>
            <dd className="money">{formatIdr(booking.service_fee_idr)}</dd>
          </>
        )}
        <dt className="totals__grand">{t("checkout.total")}</dt>
        <dd className="totals__grand money">{formatIdr(booking.total_idr)}</dd>
      </dl>

      {error && <ErrorNote>{error}</ErrorNote>}

      {booking.state === "PAID" ? (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => navigate(`/tickets/${booking.ref}`)}
        >
          {t("ticket.title")}
        </button>
      ) : (
        <button type="button" className="btn btn--primary" disabled={busy} onClick={pay}>
          {busy ? t("common.loading") : t("checkout.pay")}
        </button>
      )}

      <Note>{t("checkout.note")}</Note>
    </Shell>
  );
}
